/** A board's colour, icon and column limits on screen. Pure. */

/** The colour keys the API accepts (BOARD_COLORS), in picker order. */
export const BOARD_COLORS = ['slate', 'blue', 'teal', 'green', 'amber', 'orange', 'red', 'purple', 'pink'] as const
export type BoardColor = (typeof BOARD_COLORS)[number]

// Literal class names so Tailwind keeps them; the swatches are design tokens (index.css).
export const BOARD_SWATCH: Record<BoardColor, string> = {
  slate: 'bg-board-slate',
  blue: 'bg-board-blue',
  teal: 'bg-board-teal',
  green: 'bg-board-green',
  amber: 'bg-board-amber',
  orange: 'bg-board-orange',
  red: 'bg-board-red',
  purple: 'bg-board-purple',
  pink: 'bg-board-pink',
}
export const BOARD_TEXT: Record<BoardColor, string> = {
  slate: 'text-board-slate',
  blue: 'text-board-blue',
  teal: 'text-board-teal',
  green: 'text-board-green',
  amber: 'text-board-amber',
  orange: 'text-board-orange',
  red: 'text-board-red',
  purple: 'text-board-purple',
  pink: 'text-board-pink',
}

export function isBoardColor(v: unknown): v is BoardColor {
  return typeof v === 'string' && (BOARD_COLORS as readonly string[]).includes(v)
}

/** The board's text-colour class (its icon tint), or the default primary. */
export function boardTint(color: string | null | undefined): string {
  return isBoardColor(color) ? BOARD_TEXT[color] : 'text-primary'
}

/** The board icon keys the picker offers (lucide names); the API accepts any lucide-style key. */
export const BOARD_ICON_KEYS = [
  'kanban',
  'rocket',
  'flask',
  'shield',
  'bug',
  'clipboard',
  'wrench',
  'truck',
  'users',
  'star',
  'flag',
  'zap',
] as const
export type BoardIconKey = (typeof BOARD_ICON_KEYS)[number]

export function isBoardIcon(v: unknown): v is BoardIconKey {
  return typeof v === 'string' && (BOARD_ICON_KEYS as readonly string[]).includes(v)
}

export type WipState = 'none' | 'ok' | 'at' | 'over'

/** A column's count against its work-in-progress limit: warn at the limit, flag over it. */
export function wipState(count: number, limit: number | undefined): WipState {
  if (!limit || limit < 1) return 'none'
  if (count > limit) return 'over'
  if (count === limit) return 'at'
  return 'ok'
}

/**
 * The limits form ({ columnId: "5" }) → the API's { columnId: 5 }. Blank
 * clears a column; anything that is not a whole number from 1 to 999 is an
 * error naming the column.
 */
export function parseWipLimits(raw: Record<string, string>): { limits: Record<string, number>; invalid: string[] } {
  const limits: Record<string, number> = {}
  const invalid: string[] = []
  for (const [col, value] of Object.entries(raw)) {
    const v = value.trim()
    if (!v) continue
    const n = Number(v)
    if (Number.isInteger(n) && n >= 1 && n <= 999) limits[col] = n
    else invalid.push(col)
  }
  return { limits, invalid }
}
